import React, { useState } from "react";
import { useForm } from "react-hook-form";
import DateRangePicker from "../common/DateRangePicker";
import ScheduleConflictModal from "../modals/ScheduleConflictModal";
import { useSchedular } from "../../hooks/useSchedular";
import { CreateScheduleInput, TimeSlot } from "../../schemas/scheduleSchema";
import { useNavigate } from "react-router-dom";
// Helper function to format date in local timezone (YYYY-MM-DD)
// This avoids timezone conversion issues with toISOString()
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

interface SchedularStep3Props {
  screenIds: string[];
  playlistIds: string[];
}

const SchedularStep3: React.FC<SchedularStep3Props> = ({
  screenIds,
  playlistIds,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const [timeSlots, setTimeSlots] = useState([{ start: "", end: "" }]);
  const [playMode, setPlayMode] = useState("rotation");
  const [conflictData, setConflictData] = useState<any[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formData, setFormData] = useState<CreateScheduleInput | null>(null);
  const [newScheduleData, setNewScheduleData] = useState<any>(null);
  const navigate = useNavigate();

  // Days of week - all selected by default
  const daysOfWeekOptions = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const [selectedDays, setSelectedDays] = useState<string[]>(daysOfWeekOptions);

  const { createSchedule } = useSchedular();

  // Transform backend conflict format to modal format
  // Structure: conflicts[] -> each has {screenId, screenName, playlistId, playlistName, conflicts[]}
  // Each conflict in conflicts[] has {type, existingSchedule, conflictDetails}
  const transformConflictsToModalFormat = (conflicts: any[]): any[] => {
    return conflicts.map((screenConflict) => {
      const screenName = screenConflict.screenName || "Unknown Screen";

      // Transform each conflict detail into a schedule entry
      const schedules = (screenConflict.conflicts || []).map(
        (conflictDetail: any) => {
          const existingSchedule = conflictDetail.existingSchedule || {};
          const conflictDetails = conflictDetail.conflictDetails || {};
          const overlappingTimes = conflictDetails.overlappingTimes || {};
          const overlappingDates = conflictDetails.overlappingDates || {};

          // Get time range from overlapping times or existing schedule
          const startTime =
            overlappingTimes.start || existingSchedule.startTime || "N/A";
          const endTime =
            overlappingTimes.end || existingSchedule.endTime || "N/A";
          const timeRange =
            startTime !== "N/A" && endTime !== "N/A"
              ? `${startTime} - ${endTime}`
              : "All Day";

          // Get date range from overlapping dates or existing schedule
          let dateRange: string | undefined;
          if (overlappingDates.start && overlappingDates.end) {
            const start = new Date(overlappingDates.start).toLocaleDateString();
            const end = new Date(overlappingDates.end).toLocaleDateString();
            dateRange = `${start} to ${end}`;
          } else if (existingSchedule.startDate && existingSchedule.endDate) {
            const start = new Date(
              existingSchedule.startDate
            ).toLocaleDateString();
            const end = new Date(existingSchedule.endDate).toLocaleDateString();
            dateRange = `${start} to ${end}`;
          }

          // Get playlist name
          const playlistName =
            existingSchedule.playlistName ||
            screenConflict.playlistName ||
            "Unknown Playlist";

          // Determine if rotation or time slot
          const adGroup = existingSchedule.isOverride
            ? "Rotation"
            : "Time Slot";

          // Get overlapping days
          const overlappingDays =
            conflictDetails.overlappingDays ||
            existingSchedule.daysOfWeek ||
            [];
          const daysText =
            overlappingDays.length > 0 ? overlappingDays.join(", ") : undefined;

          return {
            timeRange,
            playlistName,
            adGroup,
            dateRange,
            daysText,
            conflictMessage: conflictDetails.message,
          };
        }
      );

      return {
        screenName,
        location: "Location not specified", // Screen address not in conflict data
        status: "ON", // Default status
        schedules,
      };
    });
  };

  // Format new schedule data for display
  const formatNewScheduleData = (
    payload: CreateScheduleInput,
    playlistIds: string[]
  ): any => {
    const startDate = payload.startDate
      ? new Date(payload.startDate).toLocaleDateString()
      : "N/A";
    const endDate = payload.endDate
      ? new Date(payload.endDate).toLocaleDateString()
      : "N/A";
    const dateRange = `${startDate} to ${endDate}`;

    let timeRange = "All Day";
    if (payload.timeSlots && payload.timeSlots.length > 0) {
      timeRange = payload.timeSlots
        .map((slot) => `${slot.startTime} - ${slot.endTime}`)
        .join(", ");
    }

    const daysText =
      payload.daysOfWeek && payload.daysOfWeek.length > 0
        ? payload.daysOfWeek.join(", ")
        : "All Days";

    return {
      dateRange,
      timeRange,
      daysText,
      adGroup: payload.isOverride ? "Rotation" : "Time Slot",
      playlistCount: playlistIds.length,
    };
  };

  // Transform summary format to modal format (fallback)
  const transformConflictsFromSummary = (summary: any): any[] => {
    // If summary contains screen-level data
    if (summary.screens && Array.isArray(summary.screens)) {
      return summary.screens.map((screen: any) => ({
        screenName: screen.name || screen.screenName || "Unknown Screen",
        location: screen.address || screen.location || "Location not specified",
        status: screen.isActive ? "ON" : "OFF",
        schedules: screen.conflicts || screen.schedules || [],
      }));
    }

    // If summary contains conflict count info
    return [
      {
        screenName: summary.totalConflicts
          ? `${summary.totalConflicts} conflict(s) detected`
          : "Multiple screens",
        location: "See details below",
        status: "N/A",
        schedules: summary.conflicts || [],
      },
    ];
  };

  const handleAddSlot = () =>
    setTimeSlots([...timeSlots, { start: "", end: "" }]);

  const handleRemoveSlot = (index: number) => {
    setTimeSlots(timeSlots.filter((_, i) => i !== index));
  };

  const handleTimeChange = (
    index: number,
    field: "start" | "end",
    value: string
  ) => {
    const updatedSlots = [...timeSlots];
    updatedSlots[index][field] = value;
    setTimeSlots(updatedSlots);
  };

  const handleDayToggle = (day: string) => {
    setSelectedDays((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day].sort(
            (a, b) =>
              daysOfWeekOptions.indexOf(a) - daysOfWeekOptions.indexOf(b)
          )
    );
  };

  const handleSelectAllDays = () => {
    setSelectedDays(daysOfWeekOptions);
  };

  const handleDeselectAllDays = () => {
    setSelectedDays([]);
  };

  const onSubmit = async (data: any) => {
    if (!data.dateRange?.startDate || !data.dateRange?.endDate) {
      alert("Please select a date range");
      return;
    }

    // Prepare payload based on play mode
    let payload: CreateScheduleInput;

    if (playMode === "timeSlot") {
      // Validate time slots
      if (timeSlots.some((slot) => !slot.start || !slot.end)) {
        alert("Please fill in all time slots");
        return;
      }

      // Convert time slots to the format expected by the backend
      const timeSlotsFormatted: TimeSlot[] = timeSlots.map((slot) => ({
        startTime: slot.start,
        endTime: slot.end,
      }));

      payload = {
        screenIds: screenIds,
        playlistIds: playlistIds,
        startDate: formatDateLocal(data.dateRange.startDate),
        endDate: formatDateLocal(data.dateRange.endDate),
        timeSlots: timeSlotsFormatted,
        daysOfWeek: selectedDays.length > 0 ? selectedDays : undefined,
        isOverride: false,
        replaceExisting: false, // Will be set to true if user confirms overwrite
      };
    } else {
      // Rotation mode
      payload = {
        screenIds: screenIds,
        playlistIds: playlistIds,
        startDate: formatDateLocal(data.dateRange.startDate),
        endDate: formatDateLocal(data.dateRange.endDate),
        daysOfWeek: selectedDays.length > 0 ? selectedDays : undefined,
        isOverride: true,
        replaceExisting: false, // Will be set to true if user confirms overwrite
      };
    }

    // Store the payload for later use if conflicts occur
    setFormData(payload);

    // Try to create schedule - it will throw error if conflicts exist
    try {
      await createSchedule(payload);
      navigate("/screens");
    } catch (error: any) {
      // Get GraphQL errors - ApolloError has graphQLErrors as a direct property
      const graphQLErrors = error?.graphQLErrors || [];
      const graphQLError = graphQLErrors[0];

      // Check if error message indicates conflict
      const isConflictError =
        error?.message?.toLowerCase().includes("conflict") ||
        graphQLError?.message?.toLowerCase().includes("conflict") ||
        graphQLError?.extensions?.code === "SCHEDULE_CONFLICT";

      if (isConflictError) {
        // Get conflicts from extensions
        const conflicts = graphQLError?.extensions?.conflicts;
        const summary = graphQLError?.extensions?.summary;

        if (conflicts && Array.isArray(conflicts) && conflicts.length > 0) {
          // Transform backend conflict format to modal format
          const transformedConflicts =
            transformConflictsToModalFormat(conflicts);

          // Prepare new schedule data for comparison
          const newSchedule = formatNewScheduleData(payload, playlistIds);
          setNewScheduleData(newSchedule);
          setConflictData(transformedConflicts);
          setShowConfirmModal(true);
        } else if (summary) {
          // If summary exists but no conflicts array, try to extract from summary
          const transformedConflicts = transformConflictsFromSummary(summary);
          setConflictData(transformedConflicts);
          setShowConfirmModal(true);
        } else {
          // If it's a conflict but no conflict details, show generic message
          setConflictData([
            {
              screenName: "Unknown",
              location: "Multiple screens",
              status: "N/A",
              schedules: [
                {
                  timeRange: "N/A",
                  playlistName: "See error details",
                  adGroup: "N/A",
                },
              ],
            },
          ]);
          setShowConfirmModal(true);
        }
      }
      // Other errors are handled by the mutation's onError
    }
  };

  // Handler for confirming overwrite
  const handleConfirmOverwrite = async () => {
    if (formData) {
      // Set replaceExisting to true and retry
      const payloadWithReplace = {
        ...formData,
        replaceExisting: true,
      };

      try {
        await createSchedule(payloadWithReplace);
        setShowConfirmModal(false);
        setFormData(null);
        setConflictData([]);
        setNewScheduleData(null);
        navigate("/screens");
      } catch (error) {
        // Error will be handled by mutation's onError
      }
    }
  };

  // Handler for cancel
  const handleCancel = () => {
    setShowConfirmModal(false);
    setFormData(null);
    setConflictData([]);
    setNewScheduleData(null);
  };

  // Comment out these functions as they're no longer needed
  // const handleConfirmOverwrite = () => {
  //   if (formData) {
  //     createMultipleAssignments(formData);
  //     setShowConfirmModal(false);
  //     setFormData(null);
  //   }
  // };

  // const handleCancel = () => {
  //   setShowConfirmModal(false);
  //     setFormData(null);
  //     setConflictData([]);
  // };

  return (
    <>
      <div className="mb-8 w-2/3 mx-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-6">
          {/* Date Range Picker */}
          <DateRangePicker
            label="Select Date Range"
            name="dateRange"
            error={errors.dateRange}
            onChange={(value) => setValue("dateRange", value)}
          />

          {/* Days of Week Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium mb-2">
              Select Days of Week
            </label>
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={handleSelectAllDays}
                className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleDeselectAllDays}
                className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded"
              >
                Deselect All
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {daysOfWeekOptions.map((day) => (
                <label
                  key={day}
                  className="flex items-center gap-2 px-3 py-2 border rounded cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedDays.includes(day)}
                    onChange={() => handleDayToggle(day)}
                    className="cursor-pointer"
                  />
                  <span className="text-sm">{day}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Play Mode */}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="rotation"
                checked={playMode === "rotation"}
                onChange={() => setPlayMode("rotation")}
              />
              <span>Keep playing by rotation</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="timeSlot"
                checked={playMode === "timeSlot"}
                onChange={() => setPlayMode("timeSlot")}
              />
              <span>Play by Time Slot</span>
            </label>
          </div>

          {/* Time Slots */}
          {playMode === "timeSlot" && (
            <div className="space-y-4">
              <button
                type="button"
                className="px-3 py-1 bg-green-600 text-white rounded shadow"
                onClick={handleAddSlot}
              >
                + Add Time Slot
              </button>

              {timeSlots.map((slot, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="time"
                    step="1"
                    value={slot.start}
                    onChange={(e) =>
                      handleTimeChange(index, "start", e.target.value)
                    }
                    className="border rounded px-2 py-1 w-40"
                  />
                  <span>-</span>
                  <input
                    type="time"
                    step="1"
                    value={slot.end}
                    onChange={(e) =>
                      handleTimeChange(index, "end", e.target.value)
                    }
                    className="border rounded px-2 py-1 w-40"
                  />
                  {timeSlots.length > 1 && (
                    <button
                      type="button"
                      className="px-2 py-1 bg-red-600 text-white rounded"
                      onClick={() => handleRemoveSlot(index)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="mt-4 px-4 py-2 bg-primary-default text-white rounded"
          >
            Submit
          </button>
        </form>
      </div>

      <ScheduleConflictModal
        isOpen={showConfirmModal}
        conflicts={conflictData}
        newSchedule={newScheduleData}
        onClose={handleCancel}
        onConfirm={handleConfirmOverwrite}
      />
    </>
  );
};

export default SchedularStep3;
